import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Alert, Paper, Chip, Grid,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import axios from 'axios';

/**
 * Reusable AI image generator component.
 *
 * Props:
 *   productTitle  - string, the product name
 *   imageUrls     - string[], existing product image URLs (used as reference)
 *   storeId       - string, Shopify store ID (optional, for tracking)
 *   onGenerated   - callback(generatedImages) called with array of { base64, mimeType }
 */
const ProductImageGenerator = ({ productTitle = '', imageUrls = [], storeId = '', onGenerated }) => {
  const [style, setStyle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedImages, setGeneratedImages] = useState([]);

  const handleGenerate = async () => {
    setError('');
    setLoading(true);
    setGeneratedImages([]);

    try {
      const response = await axios.post('/api/image-generate', {
        imageUrls,
        productTitle,
        style: style.trim(),
        storeId,
      });

      const images = response.data?.generatedImages || [];
      setGeneratedImages(images);
      if (onGenerated) onGenerated(images);
    } catch (err) {
      setError(err.response?.data?.error || 'Image generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        AI Image Generation
      </Typography>

      {productTitle && (
        <Chip label={productTitle} size="small" sx={{ mb: 1.5 }} />
      )}

      <TextField
        label="Style instructions (optional)"
        variant="outlined"
        fullWidth
        value={style}
        onChange={(e) => setStyle(e.target.value)}
        placeholder="e.g. white background, studio lighting, lifestyle shot on marble"
        size="small"
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
        onClick={handleGenerate}
        disabled={loading || !productTitle}
        sx={{ mb: 2 }}
      >
        {loading ? 'Generating...' : 'Generate AI Image'}
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {generatedImages.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Generated Images ({generatedImages.length})
          </Typography>
          <Grid container spacing={2}>
            {generatedImages.map((img, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Paper elevation={2} sx={{ p: 1, textAlign: 'center' }}>
                  <img
                    src={`data:${img.mimeType};base64,${img.base64}`}
                    alt={`Generated ${idx + 1}`}
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4 }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      href={`data:${img.mimeType};base64,${img.base64}`}
                      download={`ai-product-${idx + 1}.png`}
                    >
                      Download
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ProductImageGenerator;
